import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { baseUrl } from 'libs/core/src/lib/configs/baseUrl';
import { FilesService } from 'libs/core/src/lib/services/files.service';
import { RepresentativeService } from 'libs/core/src/lib/services/representative.service';
import {
  createQueryParams,
  IBrokerConfiguration,
  IbrokerDocuments,
  IBrokerDocumentUpload,
  IBrokerDocumentUploadRequest,
  IBrokerQueryParams,
  IbrokerStats,
  IInsurer,
  IInsurerQueryParams,
  IIntermediary,
  InsurerRepresentative,
  IntermediaryPermissions,
  IPaginatedResponse,
  IRepresentative,
  IRepresentativeRequest,
  IUserQueryParams,
  PaginatedResponse,
} from '@inshuwa-client/models';
import { IUsersService } from './users.service';
import { Observable, switchMap } from 'rxjs';
import { NgHttpCachingHeaders } from 'ng-http-caching';

@Injectable({
  providedIn: 'root',
})
export class BrokerUserManagementService implements IUsersService {
  readonly uploadService = inject(FilesService);

  constructor(
    private rep: RepresentativeService,
    private http: HttpClient,
  ) {}

  getBrokers(
    queryParams?: Partial<IBrokerQueryParams>,
  ): Observable<IPaginatedResponse<IIntermediary>> {
    return this.http.get<IPaginatedResponse<IIntermediary>>(
      `${baseUrl()}/brokers`,
      {
        params: createQueryParams(queryParams),
        headers: {
          [NgHttpCachingHeaders.DISALLOW_CACHE]: '1',
        },
      },
    );
  }

  getBrokerDocument(id: number): Observable<IbrokerDocuments[]> {
    return this.http.get<IbrokerDocuments[]>(
      `${baseUrl()}/brokers/${id}/documents`,
    );
  }

  UploadBrokerDocuments(
    id: number,
    data: IBrokerDocumentUploadRequest,
  ): Observable<IBrokerDocumentUpload> {
    return this.uploadService
      .bulkUploadDocuments({
        bucketName: 'inshuwa',
        files: data.files,
      })
      .pipe(
        switchMap((res) => {
          return this.http.post<IBrokerDocumentUpload>(
            `${baseUrl()}/brokers/${id}/documents`,
            [
              {
                ...data,
                url: res.map((r) => r.url)[0],
              },
            ],
          );
        }),
      );
  }

  assignBrokerInsurer(data: {
    insurerId: number;
    brokerId: number;
  }): Observable<IBrokerConfiguration> {
    return this.http.post<IBrokerConfiguration>(
      `${baseUrl()}/brokers/assign-broker-to-insurer`,
      data,
    );
  }

  getAllInsurers(
    param?: Partial<IInsurerQueryParams>,
  ): Observable<PaginatedResponse<IInsurer>> {
    return this.http.get<PaginatedResponse<IInsurer>>(`${baseUrl()}/insurers`, {
      params: createQueryParams(param),
    });
  }
  getBrokerStats(): Observable<IbrokerStats> {
    return this.http.get<IbrokerStats>(`${baseUrl()}/brokers/stats`);
  }
  getUsers(
    queryParams?: Partial<IUserQueryParams> | undefined,
  ): Observable<IPaginatedResponse<IRepresentative>> {
    throw new Error('Method not implemented.');
  }

  updatePermissions(
    userId: number,
    request: Partial<IntermediaryPermissions>,
  ): Observable<IntermediaryPermissions> {
    return this.http.put<IntermediaryPermissions>(
      `${baseUrl()}/users/${userId}/permissions/intermediary`,
      request,
    );
  }

  getPermissions(userId: number): Observable<IntermediaryPermissions> {
    return this.http.get<IntermediaryPermissions>(
      `${baseUrl()}/users/${userId}/permissions/intermediary`,
    );
  }

  addUser(
    user: Partial<IRepresentativeRequest>,
  ): Observable<InsurerRepresentative> {
    const brokerId = this.rep.getLoggedInBrokerRepresentative().brokerId;
    return this.http.post<InsurerRepresentative>(
      `${baseUrl()}/brokers/${brokerId}/representatives`,
      user,
    );
  }
}
